import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import Select, { MultiValue, SingleValue, StylesConfig } from 'react-select';
import { ICellEditor, ICellEditorParams } from 'ag-grid-community';
import cn from 'classnames';

import DropdownIndicator from '@/components/common/Dropdown/components/DropdownIndicator';
import { useOnClickOutside } from '@/hooks/useOnClickOutside.hook';
import focusStyles from './dropdown-grid-styles.module.scss';
import { CONTRACT_LINES_AMOUNT_WITH_EXTRA_SPACE_BELOW } from '@/config/common.config';
import { notificationService } from '@/services/notification/notification.client';
import { BillingTypeEnum } from '@/graphql/contracts/_generated_/contracts';

export type InputSearchParams = {
  searchString: string;
  searchResults?: number;
  billingTypes?: BillingTypeEnum[];
};

export interface IAgGridCustomerDropdownEditorAsyncProps<Option> extends ICellEditorParams {
  value: Option;
  placeholder?: string;
  getOptionLabel: (option: Option) => string;
  getOptionValue: (option: Option) => any;
  handleInputChange: (params: string) => Promise<Option[]>;
  isDisabled: boolean;
  saveOnChange?: boolean;
  autofocus?: boolean;
  styles?: StylesConfig<Option>;
  searchResults?: number;
  isClearable?: boolean;
}

const FIRST_EDITABLE_FIELD = 0;

function AgGridCustomerDropdownEditorAsync<Option>(
  props: IAgGridCustomerDropdownEditorAsyncProps<Option>,
  ref: ForwardedRef<ICellEditor>
) {
  const {
    value,
    placeholder,
    getOptionLabel,
    getOptionValue,
    handleInputChange,
    isDisabled,
    api,
    saveOnChange,
    autofocus,
    styles,
    rowIndex,
    colDef,
  } = props;

  const refDropdown = useRef<any>(null);
  const refContainer = useRef<HTMLDivElement | null>(null);
  const [optionsList, setOptionsList] = useState<Option[]>([]);
  const [currentOption, setCurrentOption] = useState<SingleValue<Option> | MultiValue<Option> | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState(true);
  const isLastRow = api.getEditingCells()[FIRST_EDITABLE_FIELD]?.rowIndex === api.getLastDisplayedRowIndex()
    && api.getRenderedNodes().length > CONTRACT_LINES_AMOUNT_WITH_EXTRA_SPACE_BELOW;
  const defaultPlaceholder = useMemo(() => {
    return value ? getOptionLabel(value) : 'Select';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useOnClickOutside(refContainer, () => {
    setIsDropdownOpen(false);
  });

  const onChange = (selectedOption: SingleValue<Option> | MultiValue<Option>) => {
    setCurrentOption(selectedOption ?? null);
    setIsDropdownOpen(false);

    if (saveOnChange) {
      setIsEditing(false);
    }
  };

  const onMenuOpen = () => {
    setIsDropdownOpen(true);
  };

  const onInputChange = (searchString: string) => {
    if (typeof handleInputChange === 'function' && searchString.length > 0) {
      setIsLoading(true);
      handleInputChange(searchString)
        .then((result: Option[]) => setOptionsList(result))
        .catch((err) => notificationService.open({
          title: 'Error',
          text: err?.message || 'Something went wrong',
          type: 'danger'
        }))
        .finally(() => setIsLoading(false));
    }
  };

  useImperativeHandle(ref, () => ({
    getValue() {
      return currentOption || null;
    },

    focusIn() {
      refDropdown.current?.focus();
    },

    isPopup() {
      return false;
    },
  }));

  useEffect(() => {
    // focusIn doesn't work on mount for some reasons
    // so this is way to focus dropdown on mount
    const focusCellPosition = api.getFocusedCell();
    const isRowFocused = focusCellPosition
      && focusCellPosition.rowIndex === rowIndex
      && focusCellPosition.column.getColId() === colDef.colId;

    if (autofocus || isRowFocused) {
      // setTimeout to call focus asynchronously after all elements are ready
      setTimeout(() => {
        refDropdown.current?.focus();
      });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    if (!isEditing && saveOnChange) {
      api.stopEditing();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isEditing]);

  useEffect(() => {
    if (optionsList.length) {
      setIsDropdownOpen(true);
    }
  }, [optionsList]);

  return (
    <div ref={refContainer}>
      <Select
        ref={refDropdown}
        options={optionsList}
        className={cn('ag-dropdown', focusStyles['custom-focus'])}
        classNamePrefix="ag-dropdown"
        components={{ DropdownIndicator }}
        isLoading={isLoading}
        placeholder={placeholder ?? defaultPlaceholder}
        onChange={onChange}
        onInputChange={onInputChange}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        isSearchable={true}
        isDisabled={isDisabled}
        onMenuOpen={onMenuOpen}
        menuIsOpen={isDropdownOpen}
        menuPortalTarget={document.querySelector('body')}
        filterOption={() => true}
        styles={styles}
        menuPlacement={isLastRow ? 'top' : 'auto'}
        isClearable={true}
      />
    </div>
  );
}

export default forwardRef(AgGridCustomerDropdownEditorAsync);
